import { useState, type FormEvent } from 'react';
import { GADGET_CATEGORIES, LIFECYCLE_STATES } from '@gc/domain';
import type { ApiClient, GadgetRow, Permission } from '@gc/api-client';
import { useResource } from '@gc/ui-state';
import { GadgetDetail, type GadgetDetailApi } from './GadgetDetail.js';
import { GadgetForm } from './GadgetForm.js';
import { ImportCsv } from './ImportCsv.js';
import { stateLabel, words } from './labels.js';

export type { GadgetRow } from '@gc/api-client';
export type GadgetsApi = GadgetDetailApi & Pick<ApiClient, 'importCsv'>;

type Mode = 'list' | 'add' | 'import';
type Filter = { search: string; status: string; category: string; expiring: boolean };

export function Gadgets({ api, permissions }: { api: GadgetsApi; permissions: readonly Permission[] }) {
  const canEdit = permissions.includes('gadgets:edit');
  const [mode, setMode] = useState<Mode>('list');
  const [draft, setDraft] = useState('');
  const [filter, setFilter] = useState<Filter>({ search: '', status: '', category: '', expiring: false });
  const [openId, setOpenId] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const { load } = useResource(
    () => (filter.expiring
      ? api.lifecycle.expiring(90)
      : api.gadgets.list({ search: filter.search, status: filter.status, category: filter.category })),
    [filter.search, filter.status, filter.category, filter.expiring, version],
  );

  const refresh = () => setVersion((n) => n + 1);
  const search = (e: FormEvent) => {
    e.preventDefault();
    setFilter((f) => ({ ...f, search: draft.trim() }));
  };
  const filtered = Boolean(filter.search || filter.status || filter.category || filter.expiring);

  return (
    <section>
      <h2>Gadgets</h2>

      <div className="toolbar">
        <form className="toolbar" onSubmit={search} role="search">
          <input
            type="search"
            placeholder="Tag, serial, make or model"
            aria-label="Search gadgets"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid="gadget-search"
          />
          <button type="submit">Search</button>
        </form>
        <select
          aria-label="Filter by status"
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          data-testid="filter-status"
        >
          <option value="">Any status</option>
          {LIFECYCLE_STATES.map((s) => <option key={s} value={s}>{stateLabel(s)}</option>)}
        </select>
        <select
          aria-label="Filter by category"
          value={filter.category}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
          data-testid="filter-category"
        >
          <option value="">Any category</option>
          {GADGET_CATEGORIES.map((c) => <option key={c} value={c}>{words(c)}</option>)}
        </select>
        <label className="inline">
          <input
            type="checkbox"
            checked={filter.expiring}
            onChange={(e) => setFilter((f) => ({ ...f, expiring: e.target.checked }))}
            data-testid="filter-expiring"
          />
          Warranty ending within 90 days
        </label>
        {canEdit && (
          <>
            <button type="button" className="primary" onClick={() => setMode('add')} data-testid="action-add">Add gadget</button>
            <button type="button" onClick={() => setMode('import')} data-testid="action-import">Import CSV</button>
          </>
        )}
      </div>

      {mode === 'add' && (
        <GadgetForm
          api={api}
          mode={{ kind: 'create' }}
          onCancel={() => setMode('list')}
          onDone={() => { setMode('list'); refresh(); }}
        />
      )}
      {mode === 'import' && <ImportCsv api={api} onCancel={() => setMode('list')} onImported={refresh} />}

      {load.kind === 'loading' && <p>Loading gadgets…</p>}
      {load.kind === 'error' && <p data-testid="gadgets-error">Could not load gadgets: {load.message}</p>}
      {load.kind === 'ready' && (load.data.items.length === 0
        ? (
          <p data-testid="gadgets-empty">
            {filtered ? 'Nothing matches.' : 'No gadgets yet. Import a fleet or add one to get started.'}
          </p>
        )
        : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Asset tag</th>
                  <th scope="col">Device</th>
                  <th scope="col">Status</th>
                  <th scope="col"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {load.data.items.map((g: GadgetRow) => (
                  <tr key={g.id}>
                    <td><code data-testid={`gadget-id-${g.shortId}`}>{g.shortId}</code></td>
                    <td><strong>{g.assetTag}</strong></td>
                    <td>{g.make} {g.model}</td>
                    <td><span className={`pill ${g.status}`}>{stateLabel(g.status)}</span></td>
                    <td className="row-action">
                      <button
                        type="button"
                        onClick={() => setOpenId(g.id)}
                        aria-label={`Open ${g.assetTag}`}
                        data-testid={`open-${g.shortId}`}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {openId && (
        <div data-testid="selected-gadget">
          <GadgetDetail
            key={openId}
            gadgetId={openId}
            api={api}
            permissions={permissions}
            onClose={() => setOpenId(null)}
            onChanged={refresh}
          />
        </div>
      )}
    </section>
  );
}
